import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import TextField from 'core-system/TextField'
import Dropdown from 'core-system/Dropdown'
import FlexContainer from 'core-system/FlexContainer'
import Text from 'core-system/Text'
import PrimaryButton from 'core-system/Button/PrimaryButton'
import { ProcessedReceipt } from './ReceiptReviewModal'
import {
  EMPLOYEE_NOTE_MAX_LENGTH,
  getEmployeeNoteCharacterCount,
  parseDateTime,
} from '../../utils'
import { CurrencyType } from 'features/Programs/Shared/types'
import palette from 'core-system/Themes/palette'

const CURRENCY_OPTIONS: CurrencyType[] = [
  'USD',
  'CAD',
  'EUR',
  'PLN',
  'UAH',
  'GBP',
  'JPY',
  'SGD',
]

interface InlineReceiptEditorProps {
  receipt: ProcessedReceipt | null
  onSave: (updatedReceipt: ProcessedReceipt) => void
  onRemove: () => void
}

const InlineContainer = styled.div`
  padding: 1rem 1.25rem;
  background-color: ${(props) => props.theme.palette.grey.grey50};
`

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const FormRow = styled(FlexContainer)`
  gap: 1rem;
  flex-wrap: wrap;

  > * {
    flex: 1 1 140px;
  }
`

const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const FieldLabel = styled(Text)`
  margin-bottom: 0.75rem;
  color: ${(props) => props.theme.palette.text.secondary};
`

const ActionsRow = styled(FlexContainer)`
  margin-top: 1rem;
  justify-content: space-between;
  align-items: center;
`

const RemoveText = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  cursor: pointer;
  color: ${(props) => props.theme.palette.error.red500};
  font-size: 0.875rem;
  font-weight: 600;
`

const InlineReceiptEditor = React.memo(
  ({ receipt, onSave, onRemove }: InlineReceiptEditorProps) => {
    const [amount, setAmount] = useState('')
    const [currency, setCurrency] = useState<CurrencyType | ''>('')
    const [date, setDate] = useState('')
    const [time, setTime] = useState('')
    const [employeeNote, setEmployeeNote] = useState('')

    useEffect(() => {
      if (receipt) {
        const dateTime = parseDateTime(receipt.receiptDate)
        setAmount(((receipt.merchantAmount || receipt.amount) / 100).toFixed(2))
        setCurrency(receipt.merchantCurrency as CurrencyType)
        setDate(dateTime.date)
        setTime(dateTime.time)
        setEmployeeNote(receipt.employeeNote || '')
      }
    }, [receipt])

    if (!receipt) return null

    const isDoneDisabled =
      !amount.trim() ||
      parseFloat(amount || '0') <= 0 ||
      !date.trim() ||
      !time.trim() ||
      !(currency || receipt.merchantCurrency)

    const handleSave = () => {
      const parsedAmount = parseFloat(amount || '0')
      const dateTimeString = `${date}T${time}:00`
      const amountInCents = Math.round(parsedAmount * 100)

      onSave({
        ...receipt,
        amount: amountInCents,
        merchantAmount: amountInCents,
        merchantCurrency: currency || receipt.merchantCurrency,
        receiptDate: new Date(dateTimeString).toISOString(),
        employeeNote: employeeNote.trim() ? employeeNote.trim() : null,
      })
    }

    return (
      <InlineContainer>
        <FormContainer>
          <FormRow>
            <TextField
              label='Amount'
              name='amount'
              type='number'
              value={amount}
              onChange={(e) => setAmount(e.currentTarget.value)}
              style={{ borderColor: palette.grey.grey300 }}
            />
            <FieldWrapper>
              <FieldLabel variant='body1'>Currency</FieldLabel>
              <Dropdown
                items={CURRENCY_OPTIONS.map((c) => ({ id: c, text: c }))}
                active={currency || receipt.merchantCurrency}
                itemCallback={({ id }) => setCurrency(id as CurrencyType)}
                width='100%'
                selectorMaxHeight='10rem'
                minHeight='2.5rem'
              />
            </FieldWrapper>
          </FormRow>
          <FormRow>
            <TextField
              label='Date'
              name='date'
              type='date'
              value={date}
              onChange={(e) => setDate(e.currentTarget.value)}
              style={{ borderColor: palette.grey.grey300 }}
            />
            <TextField
              label='Time'
              name='time'
              type='time'
              value={time}
              onChange={(e) => setTime(e.currentTarget.value)}
              style={{ borderColor: palette.grey.grey300 }}
            />
          </FormRow>
          <TextField
            label='Note'
            name='employeeNote'
            placeholder='Add a note to this claim (optional)'
            value={employeeNote}
            onChange={(e) =>
              setEmployeeNote(
                e.currentTarget.value.slice(0, EMPLOYEE_NOTE_MAX_LENGTH)
              )
            }
            style={{ borderColor: palette.grey.grey300 }}
          />
          <Text
            variant='body2'
            color='text.secondary'
            style={{ marginTop: '-0.5rem' }}
          >
            {getEmployeeNoteCharacterCount(employeeNote)}
          </Text>
        </FormContainer>
        <ActionsRow>
          <RemoveText type='button' onClick={onRemove}>
            Remove
          </RemoveText>
          <div>
            <PrimaryButton onClick={handleSave} disabled={isDoneDisabled}>
              Done
            </PrimaryButton>
          </div>
        </ActionsRow>
      </InlineContainer>
    )
  }
)

if (process.env.NODE_ENV !== 'production') {
  InlineReceiptEditor.displayName = 'InlineReceiptEditor'
}

export default InlineReceiptEditor
